import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { UserAuth } from "../../context/AuthContext";
import { AiOutlineFileImage, AiOutlineBulb, AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import './inference-styles.css';

const SkeletonLoader = () => (
  <div className="d-flex flex-column gap-4 animate-fade-in-up">
    {/* Image Skeleton */}
    <div className="modern-card p-0 shadow-sm w-100 overflow-hidden" style={{ borderRadius: '16px', border: 'none', backgroundColor: '#fff' }}>
      <div className="skeleton-box w-100" style={{ height: '400px', borderRadius: '0' }}></div>
    </div>
    {/* Text Skeleton */}
    <div className="modern-card p-4 shadow-sm" style={{ borderRadius: '16px', background: 'white' }}>
      <div className="skeleton-box mb-4" style={{ height: '30px', width: '40%' }}></div>
      <div className="skeleton-box w-100 mb-2" style={{ height: '20px' }}></div>
      <div className="skeleton-box w-100 mb-2" style={{ height: '20px' }}></div>
      <div className="skeleton-box mb-2" style={{ height: '20px', width: '85%' }}></div>
      <div className="skeleton-box mt-4" style={{ height: '20px', width: '60%' }}></div>
    </div>
    {/* Questions Skeleton */}
    <div className="modern-card p-4 shadow-sm" style={{ borderRadius: '16px', background: 'white' }}>
      <div className="skeleton-box mb-4" style={{ height: '30px', width: '30%' }}></div>
      <div className="skeleton-box w-100 mb-3" style={{ height: '80px', borderRadius: '12px' }}></div>
      <div className="skeleton-box w-100 mb-3" style={{ height: '80px', borderRadius: '12px' }}></div>
    </div>
  </div>
);

const bloomsLevels = [
  { key: "crear", translationKey: "create", color: "#e74c3c" },
  { key: "evaluar", translationKey: "evaluate", color: "#e67e22" },
  { key: "analizar", translationKey: "analyze", color: "#f1c40f" },
  { key: "aplicar", translationKey: "apply", color: "#2ecc71" },
  { key: "entender", translationKey: "understand", color: "#3498db" },
  { key: "recordar", translationKey: "remember", color: "#9b59b6" },
];

const bloomsColorMap = {
  crear: "#e74c3c",
  evaluar: "#e67e22",
  analizar: "#f1c40f",
  aplicar: "#2ecc71",
  entender: "#3498db",
  recordar: "#9b59b6",
};

const difficultyColorMap = {
  "fácil": "#2ecc71",
  "medio": "#f1c40f",
  "difícil": "#e74c3c",
  "easy": "#2ecc71",
  "medium": "#f1c40f",
  "hard": "#e74c3c"
};

const InferenceLearning = ({ 
  idea, 
  setIdea, 
  numQuestions, 
  setNumQuestions,
  numImageQuestions,
  setNumImageQuestions,
  selectedLang,
  setSelectedLang,
  difficulty,
  setDifficulty,
  imageDifficulty,
  setImageDifficulty,
  isCheckedLevels,
  handleCheckLevels,
  loading, 
  saving,
  result, 
  handleGenerate,
  handleSave,
  handleReset,
  t 
}) => {
  const { user } = UserAuth();

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="dashboard-title animate__animated animate__fadeInDown text-plum fs-2 fw-bold">
               {t("inferenceLearning")}
            </h1>
          </div>

          <Row className="g-4 mb-5">
            {/* Top Layout: Form */}
            <Col xs={12} className="transition-all duration-300">
              <div className="pro-card p-4 p-md-5 position-relative overflow-hidden mx-auto w-100" style={{ border: '1px solid rgba(235, 33, 120, 0.1)' }}>
                {/* Background decorative blob */}
                <div 
                  className="position-absolute rounded-circle" 
                  style={{ width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(235,33,120,0.06) 0%, rgba(255,255,255,0) 70%)', top: '-100px', right: '-100px', zIndex: 0, pointerEvents: 'none' }}
                ></div>
                
                <Form onSubmit={handleGenerate} className="position-relative" style={{ zIndex: 1 }}>
                  
                  {/* Idea Input */}
                  <div className="mb-4 mb-md-5 animate-fade-in-up">
                    <Form.Label className="fw-bold fs-5 color-plum mb-3 d-flex align-items-center gap-2">
                      <AiOutlineBulb size={24} style={{ color: "var(--pink-bright)" }} /> 
                      {t("inferenceIdeaLabel") || "¿Sobre qué quieres crear la lección?"}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t("inferenceIdeaPlaceholder") || "Ej: Un astronauta descubriendo un planeta de caramelos..."}
                      value={idea}
                      onChange={(e) => setIdea(e.target.value)}
                      className="p-4 bg-light color-plum rounded-4 shadow-sm custom-input fs-5"
                      disabled={loading || saving}
                      required
                      style={{ border: '2px solid transparent', transition: 'all 0.3s ease' }}
                    />
                  </div>

                  {/* Bloom's Taxonomy Levels */}
                  <div className="mb-4 animate-fade-in-up stagger-1">
                    <Form.Label className="fw-bold text-muted small text-uppercase mb-3 ms-1 d-block" style={{ letterSpacing: '1px' }}>
                      {t("selectLevels") || "Seleccione los niveles de Bloom"}
                    </Form.Label>
                    <div className="row g-2 g-md-3">
                      {bloomsLevels.map((lvl, i) => (
                        <div key={lvl.key} className="col-6 col-sm-4 col-md-2">
                          <div 
                            className={`p-3 border rounded-4 text-center transition-all position-relative overflow-hidden ${isCheckedLevels[i] ? 'shadow-sm' : ''}`} 
                            style={{ 
                              cursor: 'pointer', 
                              backgroundColor: isCheckedLevels[i] ? `${lvl.color}12` : 'rgba(0,0,0,0.02)',
                              borderColor: isCheckedLevels[i] ? lvl.color : 'rgba(0,0,0,0.08)',
                              transition: 'all 0.25s ease',
                              borderWidth: isCheckedLevels[i] ? '2px' : '1px',
                            }} 
                            onClick={() => handleCheckLevels({ target: { checked: !isCheckedLevels[i] } }, i)}
                          >
                            <div 
                              className="position-absolute top-0 start-0 w-100" 
                              style={{ height: '3px', backgroundColor: isCheckedLevels[i] ? lvl.color : 'transparent', transition: 'all 0.25s ease' }}
                            ></div>
                            <span className={`fw-bold small ${isCheckedLevels[i] ? '' : 'text-muted'}`} style={{ color: isCheckedLevels[i] ? lvl.color : undefined }}>
                              {t(lvl.translationKey)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <small className="text-muted mt-2 d-block ms-1">
                      {t("levelsHelp") || "No seleccionar ninguno generará preguntas para todos los niveles."}
                    </small>
                  </div>

                  {/* Options Row - Refactored */}
                  <div className="d-flex flex-column gap-4 mt-4 animate-fade-in-up stagger-2">
                    
                    {/* Bloom's Configuration */}
                    <div className="p-4 p-md-5 rounded-4" style={{ backgroundColor: 'rgba(235, 33, 120, 0.03)', border: '1px dashed rgba(235, 33, 120, 0.2)' }}>
                      <h5 className="fw-bold color-plum mb-4 fs-5 d-flex align-items-center gap-2">
                        <AiOutlineBulb size={24} style={{ color: 'var(--pink-bright)' }} /> {t("textQuestionsSettings") || "Preguntas de Lectura y Comprensión"}
                      </h5>
                      <Row className="g-4">
                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mb-2 ms-1" style={{ letterSpacing: '1px' }}>
                              {(t("difficulty") || "Dificultad")}
                            </Form.Label>
                            <Form.Select
                              value={difficulty}
                              onChange={(e) => setDifficulty(e.target.value)}
                              className="p-3 bg-white border-0 color-plum rounded-4 shadow-sm cursor-pointer"
                              disabled={loading || saving}
                            >
                              <option value="-1">{t("selectDifficulty") || "Seleccione nivel"}</option>
                              <option value="fácil">{t("easy") || "Fácil"}</option>
                              <option value="medio">{t("medium") || "Medio"}</option>
                              <option value="difícil">{t("difficult") || "Difícil"}</option>
                            </Form.Select>
                            <small className="text-muted mt-2 d-block ms-2" style={{ fontSize: '0.8rem' }}>
                              {t("bloomDifficultyHelp") || "Define qué tan complejo será el texto y su vocabulario."}
                            </small>
                          </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mb-2 ms-1" style={{ letterSpacing: '1px' }}>
                              {t("numQuestionsPerLevelLabel") || "Cantidad por Nivel"}
                            </Form.Label>
                            <Form.Select
                              value={numQuestions}
                              onChange={(e) => setNumQuestions(e.target.value)}
                              className="p-3 bg-white border-0 color-plum rounded-4 shadow-sm cursor-pointer"
                              disabled={loading || saving}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                <option key={num} value={num}>{num} {num === 1 ? t('questionSingular') : t('questions')} {t('perLevel')}</option>
                              ))}
                            </Form.Select>
                            <small className="text-muted mt-2 d-block ms-2" style={{ fontSize: '0.8rem' }}>
                              {t("questionsPerLevelHelp") || "Se multiplicará por cada nivel seleccionado arriba."}
                            </small>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Visual Configuration */}
                    <div className="p-4 p-md-5 rounded-4" style={{ backgroundColor: 'rgba(52, 152, 219, 0.03)', border: '1px dashed rgba(52, 152, 219, 0.2)' }}>
                      <h5 className="fw-bold mb-4 fs-5 d-flex align-items-center gap-2" style={{ color: '#2980b9' }}>
                        <AiOutlineEye size={24} /> {t("visualQuestionsSettings") || "Preguntas de Observación Visual"}
                      </h5>
                      <Row className="g-4">
                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mb-2 ms-1" style={{ letterSpacing: '1px' }}>
                              {(t("difficulty") || "Dificultad")}
                            </Form.Label>
                            <Form.Select
                              value={imageDifficulty}
                              onChange={(e) => setImageDifficulty(e.target.value)}
                              className="p-3 bg-white border-0 color-plum rounded-4 shadow-sm cursor-pointer"
                              disabled={loading || saving}
                              style={{ color: '#2980b9' }}
                            >
                              <option value="-1">{t("selectDifficulty") || "Seleccione nivel"}</option>
                              <option value="fácil">{t("easy") || "Fácil"}</option>
                              <option value="medio">{t("medium") || "Medio"}</option>
                              <option value="difícil">{t("difficult") || "Difícil"}</option>
                              <option value="mixto">{t("mixed")}</option>
                            </Form.Select>
                            <small className="text-muted mt-2 d-block ms-2" style={{ fontSize: '0.8rem' }}>
                              {t("visualDifficultyHelp") || "Qué tan profunda debe ser la observación del estudiante."}
                            </small>
                          </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mb-2 ms-1" style={{ letterSpacing: '1px' }}>
                              {t("imageQuestionsTitle") || "Cantidad Total"}
                            </Form.Label>
                            <Form.Select
                              value={numImageQuestions}
                              onChange={(e) => setNumImageQuestions(e.target.value)}
                              className="p-3 bg-white border-0 color-plum rounded-4 shadow-sm cursor-pointer"
                              disabled={loading || saving}
                              style={{ color: '#2980b9' }}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num} {num === 1 ? t('questionSingular') : t('questions')}</option>
                              ))}
                            </Form.Select>
                            <small className="text-muted mt-2 d-block ms-2" style={{ fontSize: '0.8rem' }}>
                              {t("visualQuestionsHelp") || "Preguntas basadas únicamente en la imagen generada."}
                            </small>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Footer Row: Language and Submit */}
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-4 mt-3">
                      <div className="flex-grow-1" style={{ maxWidth: '300px' }}>
                        <Form.Group>
                          <Form.Label className="fw-bold text-muted small text-uppercase mb-2 ms-1" style={{ letterSpacing: '1px' }}>
                            {t("language") || "Idioma de la lección"}
                          </Form.Label>
                          <Form.Select
                            value={selectedLang}
                            onChange={(e) => setSelectedLang(e.target.value)}
                            className="p-3 bg-light border-0 color-plum rounded-4 shadow-sm cursor-pointer fw-bold"
                            disabled={loading || saving}
                            style={{ minHeight: '56px' }}
                          >
                            <option value="es">{t("spanish") || "Español"}</option>
                            <option value="en">{t("english") || "Inglés"}</option>
                            <option value="ta">{t("tamil") || "Tamil"}</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      
                      <div className="flex-grow-1 text-lg-end w-100" style={{ maxWidth: '400px', alignSelf: 'flex-end' }}>
                        <Button 
                          type="submit" 
                          className="pro-btn pro-btn-primary p-3 w-100 d-flex align-items-center justify-content-center gap-2 fs-5 position-relative overflow-hidden fw-bold shadow"
                          disabled={loading || saving || !idea.trim() || difficulty === "-1" || imageDifficulty === "-1"}
                          style={{ transition: 'all 0.3s ease', minHeight: '56px', borderRadius: '14px' }}
                        >
                          {loading ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                              <span>{t("generating") || "Creando lección..."}</span>
                            </>
                          ) : (
                            <span>{t("generateInference") || "Generar Contenido"} ✨</span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            </Col>

            {/* Right side Results or Loading */}
            {(result || loading) && (
              <Col xs={12} className="transition-all duration-300 mt-4">
                {loading && !result ? (
                  <SkeletonLoader />
                ) : result ? (
                  <div className="d-flex flex-column gap-4 animate-fade-in-up stagger-3">
                    <Row className="g-4">
                {/* Image Section */}
                <Col lg={12} className="d-flex align-items-start">
                  <div className="pro-card p-0 w-100 overflow-hidden" style={{ backgroundColor: 'var(--plum-bg)', height: 'fit-content' }}>
                    <div className="p-3 text-white text-center d-flex align-items-center justify-content-center gap-2 fw-bold" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                      <AiOutlineFileImage size={24} /> {t("inferenceLearning") || "Imagen Generada"}
                    </div>
                    <div className="p-0 position-relative w-100" style={{ backgroundColor: '#fff' }}>
                       <img 
                          src={result.imageUrl} 
                          alt="Inference Generated" 
                          className="img-fluid w-100" 
                          style={{ objectFit: 'contain' }}
                       />
                    </div>
                  </div>
                </Col>

                {/* Content Section */}
                <Col lg={12}>
                  <div className="pro-card p-4 p-md-5 h-100 mb-0 d-flex flex-column gap-4">
                    
                    {/* Reading Text */}
                    {result.readingText && (
                      <div className="p-4 rounded-4" style={{ backgroundColor: '#f8fafc', borderLeft: '5px solid var(--plum-bg)' }}>
                        <h4 className="fw-bold color-plum mb-3 fs-5 d-flex align-items-center gap-2">
                          <AiOutlineFileImage size={24} className="text-muted" /> {t("inferenceReadingText") || "Texto para Lectura"}
                        </h4>
                        <div className="text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {result.readingText}
                        </div>
                      </div>
                    )}

                    {/* Vocabulary List */}
                    {result.vocabulary && result.vocabulary.length > 0 && (
                      <div className="p-3" style={{ backgroundColor: 'rgba(235, 33, 120, 0.05)', borderRadius: '12px' }}>
                        <h4 className="fw-bold color-plum mb-3 fs-5">{t("vocabularyList") || "Vocabulario"}</h4>
                        <div className="d-flex flex-wrap gap-2">
                           {result.vocabulary.map((voc, idx) => (
                             <div key={idx} className="bg-white px-3 py-2 rounded-pill shadow-sm border border-light d-flex align-items-center gap-2">
                               <span className="fw-bold" style={{ color: 'var(--pink-bright)' }}>{voc.word}:</span>
                               <span className="text-muted small">{voc.definition}</span>
                             </div>
                           ))}
                        </div>
                      </div>
                    )}

                    {/* Questions */}
                    {result.questions && result.questions.length > 0 && (
                      <div>
                        <h4 className="fw-bold color-plum mb-3 fs-5">{t("inferenceQuestions") || "Preguntas de Inferencia"}</h4>
                        <div className="d-flex flex-column gap-3">
                           {result.questions.map((q, qIdx) => (
                             <Card key={qIdx} className="pro-card overflow-hidden" style={{ borderLeft: `5px solid ${bloomsColorMap[q.level?.toLowerCase()] || 'var(--plum-bg)'}` }}>
                               <Card.Header className="pro-card-header pt-4 pb-2 px-4 d-flex justify-content-between align-items-center">
                                  <span className="fw-bold fs-6" style={{ color: 'var(--plum-bg)' }}>
                                    {qIdx + 1}. {q.question}
                                  </span>
                                  {q.level && (
                                    <span 
                                      className="badge rounded-pill px-3 py-1 fw-bold text-white" 
                                      style={{ 
                                        backgroundColor: bloomsColorMap[q.level?.toLowerCase()] || 'var(--plum-bg)',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      {q.level}
                                    </span>
                                  )}
                               </Card.Header>
                               <Card.Body className="pt-2 pb-3 px-4">
                                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-2">
                                     {q.options && q.options.map((opt, oIdx) => (
                                        <li 
                                          key={oIdx} 
                                          className={`question-option ${oIdx === q.correctOptionIndex ? 'fw-bold' : 'text-muted'}`}
                                          style={{ 
                                            backgroundColor: oIdx === q.correctOptionIndex ? 'rgba(46, 204, 113, 0.1)' : 'rgba(0,0,0,0.02)',
                                            color: oIdx === q.correctOptionIndex ? '#27ae60' : 'inherit',
                                            border: oIdx === q.correctOptionIndex ? '1px solid rgba(46, 204, 113, 0.3)' : '1px solid transparent'
                                          }}
                                        >
                                           <span className="me-2 fw-bold" style={{opacity: 0.6}}>{String.fromCharCode(65 + oIdx)}.</span> {opt} {oIdx === q.correctOptionIndex && '✓'}
                                        </li>
                                     ))}
                                  </ul>
                               </Card.Body>
                             </Card>
                           ))}
                        </div>
                      </div>
                    )}

                    {/* Image Questions */}
                    {result.imageQuestions && result.imageQuestions.length > 0 && (
                      <div className="mt-2">
                        <h4 className="fw-bold color-plum mb-3 fs-5 d-flex align-items-center gap-2">
                          <AiOutlineEye size={24} className="text-muted" /> {t("imageQuestionsTitle") || "Preguntas sobre la Imagen"}
                        </h4>
                        <div className="d-flex flex-column gap-3">
                           {result.imageQuestions.map((q, qIdx) => (
                             <Card key={qIdx} className="pro-card overflow-hidden" style={{ borderLeft: `5px solid ${difficultyColorMap[q.difficulty?.toLowerCase()] || 'var(--plum-bg)'}` }}>
                               <Card.Header className="pro-card-header pt-4 pb-2 px-4 d-flex justify-content-between align-items-center">
                                  <span className="fw-bold fs-6" style={{ color: 'var(--plum-bg)' }}>
                                    {qIdx + 1}. {q.question}
                                  </span>
                                  {q.difficulty && (
                                    <span 
                                      className="badge rounded-pill px-3 py-1 fw-bold text-white" 
                                      style={{ 
                                        backgroundColor: difficultyColorMap[q.difficulty?.toLowerCase()] || 'var(--plum-bg)',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      {t(q.difficulty.toLowerCase()) || q.difficulty}
                                    </span>
                                  )}
                               </Card.Header>
                               <Card.Body className="pt-2 pb-3 px-4">
                                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-2">
                                     {q.options && q.options.map((opt, oIdx) => (
                                        <li 
                                          key={oIdx} 
                                          className={`question-option ${oIdx === q.correctOptionIndex ? 'fw-bold' : 'text-muted'}`}
                                          style={{ 
                                            backgroundColor: oIdx === q.correctOptionIndex ? 'rgba(46, 204, 113, 0.1)' : 'rgba(0,0,0,0.02)',
                                            color: oIdx === q.correctOptionIndex ? '#27ae60' : 'inherit',
                                            border: oIdx === q.correctOptionIndex ? '1px solid rgba(46, 204, 113, 0.3)' : '1px solid transparent'
                                          }}
                                        >
                                           <span className="me-2 fw-bold" style={{opacity: 0.6}}>{String.fromCharCode(65 + oIdx)}.</span> {opt} {oIdx === q.correctOptionIndex && '✓'}
                                        </li>
                                     ))}
                                  </ul>
                               </Card.Body>
                             </Card>
                           ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="d-flex justify-content-end gap-3 mt-4 pt-4 px-2 border-top">
                      <Button 
                        variant="outline-secondary" 
                        onClick={handleReset} 
                        className="px-4 py-2 fw-bold"
                        style={{ borderRadius: '10px' }}
                        disabled={saving}
                      >
                        {t("discard") || "Descartar"}
                      </Button>
                      <Button 
                        onClick={handleSave} 
                        className="btn-primary-custom px-5 py-2 fw-bold shadow-sm d-flex align-items-center gap-2"
                        style={{ borderRadius: '10px' }}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                             <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                             {t("saving") || "Guardando..."}
                          </>
                        ) : (
                          t("save") || "Guardar"
                        )}
                      </Button>
                    </div>

                  </div>
                </Col>
              </Row>
                  </div>
                ) : null}
              </Col>
            )}
          </Row>

        </Container>
      </main>
      
      {/* Reusing existing global styles from the app */}
      <style>{`
        .custom-input {
          transition: all 0.3s ease;
        }
        .custom-input:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(235, 33, 120, 0.2) !important;
          background-color: #fff !important;
        }
        .color-plum {
          color: var(--plum-bg) !important;
        }
        .transition-all {
          transition: all 0.25s ease;
        }
      `}
      </style>
    </div>
  );
};

export default InferenceLearning;
